import { useAccountTags } from "@/api/hooks/account";
import { useMarketplaceDashboard, useUpdateMarketplaceDashboardPublication } from "@/api/hooks/marketplace";
import { setProfileTags } from "@/api/hooks/profile";
import { useUser } from "@/api/hooks/user";
import { Card } from "@/components/atoms/Card/Card";
import { ScrollableArea } from "@/components/atoms/ScrollableArea/ScrollableArea";
import { ChipGroup } from "@/components/molecules/ChipGroup/ChipGroup";
import { DashboardCardHeader } from "@/components/molecules/DashboardCardHeader/DashboardCardHeader";
import { DropdownMenu } from "@/components/molecules/DropdownMenu/DropdownMenu";
import { OptionsMenu, type OptionsMenuItem } from "@/components/molecules/OptionsMenu/OptionsMenu";
import { TabChip } from "@/components/molecules/TabChip/TabChip";
import { generateCheckboxGroupItems } from "@/components/organisms/DashboardCard/util";
import ManagedTags from "@/components/organisms/ManagedTags/ManagedTags";
import { EditModal } from "@/marketplace/edit-modal";
import { isMarketplaceEditor } from "@/marketplace/utils";
import { DatetimeLabel } from "@/overlay/components/CommonParts/DatetimeLabel";
import {
  BufferName,
  useLocalClipboard,
} from "@/overlay/components/LayerStackWindow/ManageActionPanel/LocalClipboardContext";
import { useTabManagement } from "@/overlay/overlays/default/NavigationHeader/hooks/useTabManagement";
import { getFeatureValue } from "@/types/featureSet";
import { classNames } from "@/utility/jsx";
import { plural, t } from "@lingui/core/macro";
import { GuiTimeZone, type SlimProfile } from "@mm/metx-workbench.meteomatics.com";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { DashboardCardTagGroup } from "../DashboardCardTagGroup/DashboardCardTagGroup";
import "./DashboardCard.scss";

export interface DashboardCardProps {
  profile: SlimProfile;
  isPinned: boolean;
  onPin: (id: string) => void;
  isExpanded: boolean;
  onDelete: (id: string) => void;
  onEditTitle: (profile: SlimProfile) => void;
  onTabSelect: (profileId: number, tabId: number) => void;
  onDuplicate: (profileId: number) => void;
  activeDashboard?: number;
  variant?: "default" | "preview";
  openTabId?: number;
}

export const DashboardCard: React.FC<DashboardCardProps> = ({
  profile,
  isPinned,
  onPin,
  onDelete,
  onEditTitle,
  onTabSelect,
  onDuplicate,
  isExpanded,
  activeDashboard,
  variant = "default",
  openTabId,
}) => {
  const isPreview = variant === "preview";
  const { tags } = useAccountTags();
  const dispatch = useDispatch<any>();
  const { hasData: hasDataInClipboard } = useLocalClipboard<string>();
  const { handleTabPaste, handleTabCopy } = useTabManagement();
  const { features, user } = useUser();
  const canEditDashboard = getFeatureValue(features, "ui.dashboard.edit") !== false;
  const canEditMarketplace = isMarketplaceEditor(user);
  const [isMarketplaceModalOpen, setIsMarketplaceModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const { data: marketplaceDashboard } = useMarketplaceDashboard(profile.id, {
    enabled: canEditMarketplace,
    retry: false,
  });
  const updatePublication = useUpdateMarketplaceDashboardPublication();
  const isPublished = marketplaceDashboard?.published ?? false;

  const handleTogglePublication = async () => {
    if (isPublishing) {
      return;
    }
    setIsPublishing(true);
    try {
      await updatePublication.mutateAsync({
        profileId: profile.id,
        publication: { published: !isPublished },
      });
    } catch (error) {
      console.error("Failed to update dashboard:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  const canPasteTab = useMemo(() => {
    return hasDataInClipboard(BufferName.Tab);
  }, [hasDataInClipboard]);

  const handleNewTags = (tags: string[]) => {
    dispatch(
      setProfileTags({
        profileId: profile.id,
        tags: tags,
      }),
    );
  };

  const managedTagItems = useMemo(() => {
    // account tags to render, profile.tags are selected
    return generateCheckboxGroupItems(tags.available ?? [], profile.tags ?? []);
  }, [tags, profile.tags]);

  const menuItems: OptionsMenuItem[] = isPreview
    ? []
    : [
        {
          id: "edit",
          label: t`Rename dashboard`,
          iconName: "edit",
          onSelect: () => onEditTitle(profile),
        },
        {
          id: "duplicate",
          label: t`Duplicate dashboard`,
          iconName: "layers",
          onSelect: () => onDuplicate(profile.id),
        },
        ...(managedTagItems.length > 0
          ? [
              {
                id: "tag",
                label: t`Manage tags`,
                iconName: "tag",
                onSelect: console.log,
                nestedComponent: (
                  <DropdownMenu isOpen={true} onClose={() => {}}>
                    <ManagedTags items={managedTagItems} onClick={handleNewTags} maxChecked={3} />
                  </DropdownMenu>
                ),
              },
            ]
          : []),
        {
          id: "delete",
          label: t`Delete dashboard`,
          iconName: "delete_outline",
          isDestructive: true,
          onSelect: () => onDelete(profile.id.toString()),
        },
        ...(canEditMarketplace
          ? [
              {
                id: "marketplace",
                label: t`Edit marketplace`,
                iconName: "storefront",
                onSelect: () => setIsMarketplaceModalOpen(true),
              },
              {
                id: "marketplace-publish",
                label: isPublished ? t`Unpublish` : t`Publish`,
                iconName: isPublished ? "block" : "publish",
                disabled: isPublishing,
                onSelect: () => void handleTogglePublication(),
              },
            ]
          : []),
      ];

  if (!isPreview && canPasteTab) {
    menuItems.unshift({
      id: "paste-tab",
      label: t`Paste tab`,
      iconName: "content_paste",
      onSelect: () => {
        handleTabPaste(profile.id);
      },
    });
  }

  const chipTabs = useMemo(() => {
    return [...profile.tabs]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((tab) => ({
        id: tab.id.toString(),
        title: tab.title ?? "(unnamed)",
      }));
  }, [profile.tabs]);

  const activeTabIdString = profile.tab_active;
  const fallbackTabId = activeTabIdString || profile.tabs?.[0]?.id;
  const targetTabId = openTabId ?? fallbackTabId;
  const handleCardClick = () => {
    if (!targetTabId) {
      return;
    }
    onTabSelect(profile.id, targetTabId);
  };

  const cardClassName = classNames(
    "dashboard-card",
    isPinned ? "is-pinned" : "",
    activeDashboard === profile.id ? "active" : "",
    isPreview ? "is-preview" : "",
  );

  return (
    <Card className={cardClassName} onClick={handleCardClick}>
      <DashboardCardHeader
        title={profile.title}
        isPinned={isPinned}
        onPinClick={() => onPin(profile.id.toString())}
        actions={
          menuItems.length ? (
            <OptionsMenu items={menuItems} className={"more-button"} testId="dashboard-options-menu" />
          ) : null
        }
        showActions={!isPreview && canEditDashboard}
      />

      {!isPreview && canEditDashboard && (
        <DashboardCardTagGroup onSetTags={handleNewTags} capacity={3} tags={profile.tags} items={managedTagItems} />
      )}

      <div className="dashboard-card__meta">
        <div className="dashboard-card__meta-row">
          <span>
            {t`LAST UPDATED`}
            <DatetimeLabel
              className="dashboard-card__meta-datetime"
              datetime={DateTime.fromISO(profile.time_updated)}
              timezone={GuiTimeZone.local}
            />
          </span>
        </div>
        <div className="dashboard-card__meta-row">
          <span>
            {t`Creation Date`}
            <DatetimeLabel
              className="dashboard-card__meta-datetime"
              datetime={DateTime.fromISO(profile.time_created)}
              timezone={GuiTimeZone.local}
            />
          </span>
          <span>
            {profile.tabs.length} {plural(profile.tabs.length, { one: "tab", other: "tabs" })}
          </span>
        </div>
      </div>

      <ScrollableArea
        showScrollbar={false}
        className={classNames("dashboard-card__scrollable", isExpanded ? "is-expanded" : "")}
      >
        <ChipGroup activeTabId={activeTabIdString}>
          {chipTabs.map((tab) => (
            <TabChip
              key={tab.id}
              label={tab.title}
              onClick={() => onTabSelect(profile.id, Number.parseInt(tab.id))}
              onCopy={() => handleTabCopy(tab.id)}
              showCopy={!isPreview}
            />
          ))}
        </ChipGroup>
      </ScrollableArea>
      {canEditMarketplace && !isPreview && (
        <EditModal profile={profile} isOpen={isMarketplaceModalOpen} onClose={() => setIsMarketplaceModalOpen(false)} />
      )}
    </Card>
  );
};
